/**
 * BookingsList.jsx
 * Displays all bookings in a table with filters, search, edit, and delete.
 * Highlights upcoming check-ins within 3 days.
 */
import React, { useState, useMemo } from "react";
import { exportToCSV } from "../utils/api";

// ─── Source emoji map ─────────────────────────────────────────────────────────
const SOURCE_ICONS = {
  WhatsApp: "💬",
  "Phone Call": "📞",
  Instagram: "📸",
  Facebook: "👥",
  Airbnb: "🏠",
  "Booking.com": "🌐",
  "Walk-in": "🚶",
  Other: "📝",
};

// ─── Format currency ──────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    maximumFractionDigits: 0,
  }).format(n || 0);

// ─── Format date to readable ──────────────────────────────────────────────────
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-PK", { day: "numeric", month: "short", year: "numeric" })
    : "—";

// ─── Check if check-in is within 3 days ──────────────────────────────────────
const isUpcomingSoon = (checkIn) => {
  const today = new Date();
  const ci = new Date(checkIn);
  const diff = (ci - today) / (1000 * 60 * 60 * 24);
  return diff >= 0 && diff <= 3;
};

export default function BookingsList({ bookings, onDelete, deleteLoading, onEdit, onInvoice, onComplete, canDelete = true }) {
  const [search, setSearch] = useState("");
  const [filterProperty, setFilterProperty] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [sortField, setSortField] = useState("checkIn");
  const [sortDir, setSortDir] = useState("desc");
  const [confirmDelete,   setConfirmDelete]   = useState(null);
  const [confirmComplete, setConfirmComplete] = useState(null); // bookingId awaiting completion confirm

  // ─── Unique properties for filter dropdown ──────────────────────────────────
  const properties = useMemo(() => {
    const unique = [...new Set(bookings.map((b) => b.property).filter(Boolean))];
    return ["All", ...unique];
  }, [bookings]);

  // ─── Filter + search + sort ─────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return bookings
      .filter((b) => {
        const q = search.toLowerCase();
        const matchSearch =
          !q ||
          b.guestName?.toLowerCase().includes(q) ||
          b.property?.toLowerCase().includes(q) ||
          b.phone?.includes(q) ||
          b.bookingId?.toLowerCase().includes(q);
        const matchProp = filterProperty === "All" || b.property === filterProperty;
        const matchStatus = filterStatus === "All" || b.status === filterStatus;
        return matchSearch && matchProp && matchStatus;
      })
      .sort((a, b) => {
        let aVal = a[sortField] || "";
        let bVal = b[sortField] || "";
        if (sortField === "totalPrice" || sortField === "remaining") {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        }
        if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
  }, [bookings, search, filterProperty, filterStatus, sortField, sortDir]);

  // ─── Sort toggle ────────────────────────────────────────────────────────────
  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  const SortIcon = ({ field }) =>
    sortField === field ? (sortDir === "asc" ? " ↑" : " ↓") : " ↕";

  // ─── Delete flow ────────────────────────────────────────────────────────────
  const handleDeleteClick = (id) => setConfirmDelete(id);
  const handleDeleteConfirm = async () => {
    if (confirmDelete) {
      await onDelete(confirmDelete);
      setConfirmDelete(null);
    }
  };

  return (
    <div className="card fade-in" style={{ overflow: "hidden" }}>
      {/* ── Toolbar ── */}
      <div
        style={{
          padding: "20px 24px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          gap: "12px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <h2 style={{ fontSize: 20, flex: "1 1 auto" }}>All Bookings</h2>

        {/* Search */}
        <input
          placeholder="🔍  Search guest, property…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            background:"var(--input-bg)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            color: "var(--text)",
            fontSize: 13,
            padding: "8px 14px",
            width: 200,
            outline: "none",
            fontFamily: "Inter, sans-serif",
          }}
        />

        {/* Property filter */}
        <select
          value={filterProperty}
          onChange={(e) => setFilterProperty(e.target.value)}
          style={{
            background:"var(--input-bg)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            color: "var(--text)",
            fontSize: 13,
            padding: "8px 12px",
            fontFamily: "Inter, sans-serif",
            cursor: "pointer",
          }}
        >
          {properties.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        {/* Status filter */}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            background:"var(--input-bg)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            color: "var(--text)",
            fontSize: 13,
            padding: "8px 12px",
            fontFamily: "Inter, sans-serif",
            cursor: "pointer",
          }}
        >
          {["All", "Upcoming", "Active", "Completed", "Cancelled"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        {/* Export CSV */}
        <button
          className="st-btn st-btn-secondary"
          onClick={() => exportToCSV(filtered)}
          style={{ fontSize: 13, padding: "8px 14px" }}
        >
          📥 Export CSV
        </button>
      </div>

      {/* ── Table ── */}
      {filtered.length === 0 ? (
        <div style={{ padding: "60px", textAlign: "center", color: "var(--text-3)" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔎</div>
          <p style={{ fontSize: 15 }}>No bookings found</p>
          <p style={{ fontSize: 13, marginTop: 6 }}>Try adjusting your filters or add a new booking.</p>
        </div>
      ) : (
        <div className="table-scroll" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", minWidth: 800, borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--bg)", borderBottom: "1px solid var(--border)" }}>
                {[
                  { label: "Guest", field: "guestName" },
                  { label: "Property", field: "property" },
                  { label: "Check-in", field: "checkIn" },
                  { label: "Check-out", field: "checkOut" },
                  { label: "Nights", field: "nights" },
                  { label: "Total", field: "totalPrice" },
                  { label: "Advance", field: "advancePaid" },
                  { label: "Remaining", field: "remaining" },
                  { label: "Source", field: "source" },
                  { label: "Status", field: "status" },
                  { label: "", field: null },
                ].map(({ label, field }) => (
                  <th
                    key={label}
                    onClick={() => field && toggleSort(field)}
                    style={{
                      padding: "11px 14px",
                      textAlign: "left",
                      color: "var(--text-2)",
                      fontWeight: 500,
                      letterSpacing: "0.04em",
                      fontSize: 11,
                      textTransform: "uppercase",
                      cursor: field ? "pointer" : "default",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}{field && <SortIcon field={field} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((b, i) => {
                const soon       = isUpcomingSoon(b.checkIn) && b.status === "Upcoming";
                const isComplete = b.status === "Completed";
                const isCancelled = b.status === "Cancelled";

                return (
                  <tr
                    key={b.bookingId || i}
                    style={{
                      borderBottom: "1px solid var(--border)",
                      background: soon ? "rgba(212, 168, 83, 0.04)" : "transparent",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-card-hover)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = soon ? "rgba(212, 168, 83, 0.04)" : "transparent")}
                  >
                    {/* Guest */}
                    <td style={{ padding: "12px 14px" }}>
                      <div style={{ fontWeight: 500, color: "var(--text)" }}>{b.guestName}</div>
                      <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 1 }}>{b.phone}</div>
                    </td>

                    {/* Property */}
                    <td style={{ padding: "12px 14px", color: "var(--text-2)" }}>{b.property}</td>

                    {/* Check-in */}
                    <td style={{ padding: "12px 14px" }}>
                      <span style={{ color: soon ? "var(--gold)" : "var(--text)" }}>
                        {fmtDate(b.checkIn)}{soon && " 🔔"}
                      </span>
                    </td>

                    {/* Check-out */}
                    <td style={{ padding: "12px 14px", color: "var(--text-2)" }}>{fmtDate(b.checkOut)}</td>

                    {/* Nights */}
                    <td style={{ padding: "12px 14px", color: "var(--text-3)", textAlign: "center" }}>{b.nights}</td>

                    {/* Total */}
                    <td style={{ padding: "12px 14px", color: "var(--gold)", fontWeight: 500 }}>{fmt(b.totalPrice)}</td>

                    {/* Advance */}
                    <td style={{ padding: "12px 14px", color: "var(--success)" }}>{fmt(b.advancePaid)}</td>

                    {/* ── Remaining Payment Box ── */}
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{
                        background: b.remaining > 0 ? "var(--danger-dim)" : "var(--success-dim)",
                        border: `1px solid ${b.remaining > 0 ? "rgba(239,68,68,0.2)" : "rgba(34,197,94,0.2)"}`,
                        borderRadius: 8,
                        padding: "6px 10px",
                        minWidth: 100,
                      }}>
                        <div style={{
                          fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: b.remaining > 0 ? "var(--danger)" : "var(--success)",
                          marginBottom: 2,
                        }}>
                          {b.remaining > 0 ? "Remaining" : "Paid"}
                        </div>
                        <div style={{
                          fontSize: 13, fontWeight: 700,
                          color: b.remaining > 0 ? "var(--danger)" : "var(--success)",
                        }}>
                          {fmt(b.remaining)}
                        </div>
                      </div>
                    </td>

                    {/* Source */}
                    <td style={{ padding: "12px 14px", color: "var(--text-2)" }}>
                      {SOURCE_ICONS[b.source] || "📝"} {b.source}
                    </td>

                    {/* Status badge */}
                    <td style={{ padding: "12px 14px" }}>
                      <span className={`st-badge badge-${(b.status || "upcoming").toLowerCase()}`}>
                        {b.status}
                      </span>
                    </td>

                    {/* ── Actions ── */}
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ display:"flex", flexDirection:"column", gap:4, minWidth:100 }}>

                        {/* ── Complete Booking button ── */}
                        {!isComplete && !isCancelled && (
                          confirmComplete === b.bookingId ? (
                            <div style={{ display: "flex", gap: 4 }}>
                              <button
                                className="st-btn st-btn-secondary"
                                onClick={() => {
                                  onComplete && onComplete(b.bookingId);
                                  setConfirmComplete(null);
                                }}
                                style={{
                                  padding: "4px 10px", fontSize: 11, fontWeight: 700,
                                  background: "rgba(81,207,102,0.15)",
                                  border: "1px solid rgba(81,207,102,0.35)",
                                  color: "var(--success)",
                                }}
                              >
                                ✅ Yes
                              </button>
                              <button
                                className="st-btn st-btn-secondary"
                                onClick={() => setConfirmComplete(null)}
                                style={{ padding: "4px 8px", fontSize: 11 }}
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              className="st-btn st-btn-success st-btn-sm"
                              onClick={() => { setConfirmComplete(b.bookingId); setRemainingInput((b.remaining||0)>0?String(b.remaining):""); }}
                              title="Mark as completed"
                              style={{ width:"100%", fontSize:12, minHeight:30 }}
                            >
                              ✓ Complete
                            </button>
                          )
                        )}

                        {/* Row 2: Invoice + Edit + Delete */}
                        <div style={{ display: "flex", gap: 4 }}>
                          <button
                            className="st-btn st-btn-ghost st-btn-sm"
                            onClick={() => onInvoice && onInvoice(b)}
                            title="Generate invoice"
                            style={{ padding:"6px 8px", minHeight:30 }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/></svg>
                          </button>
                          <button
                            className="st-btn st-btn-ghost st-btn-sm"
                            onClick={() => onEdit && onEdit(b)}
                            title="Edit booking"
                            style={{ padding:"6px 8px", minHeight:30 }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          {confirmDelete === b.bookingId ? (
                            <>
                              <button className="btn btn-danger"
                                onClick={handleDeleteConfirm}
                                disabled={deleteLoading}
                                style={{ padding: "4px 7px", fontSize: 11 }}>
                                Yes
                              </button>
                              <button className="st-btn st-btn-secondary"
                                onClick={() => setConfirmDelete(null)}
                                style={{ padding: "4px 7px", fontSize: 11 }}>
                                No
                              </button>
                            </>
                          ) : (
                            <button
                              className="st-btn st-btn-ghost st-btn-sm"
                              onClick={() => handleDeleteClick(b.bookingId)}
                              title="Delete booking"
                              style={{ padding:"6px 8px", minHeight:30, color:"var(--danger)" }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3,6 5,6 21,6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Footer count ── */}
      {filtered.length > 0 && (
        <div
          style={{
            padding: "12px 24px",
            borderTop: "1px solid var(--border)",
            color: "var(--text-3)",
            fontSize: 12,
          }}
        >
          Showing {filtered.length} of {bookings.length} bookings
        </div>
      )}
    </div>
  );
}
